import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Button from '@material-ui/core/Button';
import { XGrid } from '@material-ui/x-grid';
import DeleteIcon from '@material-ui/icons/Delete';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import SaveIcon from '@material-ui/icons/Save';
import CloseIcon from '@material-ui/icons/Close';
import DateFnsUtils from '@date-io/date-fns';
import {
  MuiPickersUtilsProvider,
  KeyboardTimePicker,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import Grid from '@material-ui/core/Grid';
import { 
  savePersonalCapability, getPersonalCapabilities, removePersonalCapability
} from '../../reducers/EsnReducer';
import { findPartyPerson } from '../../reducers/EventsReducer';
import { handleError } from '../../reducers/ErrorReducer';
import { showSpinner, hideSpinner } from '../../reducers/UiReducer';

const useStyles = makeStyles(theme => ({
  wrap: {
    '& > h5': {
      marginBottom: 20,
    },
  },
  form: {
    margin: '0 -4px',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    flexGrow: 1,
  },
  autocomplete: {
    margin: '0 4px 8px',
    maxWidth: 400,
    flexBasis: 400,
    minWidth: 400,
    flexGrow: 1
  },
  buttons: {
    // marginBottom: 8,
    display: 'flex',
    alignItems: 'center',
    '& button': {
      marginLeft: theme.spacing(1),
    },
    '& svg': {
      marginRight: theme.spacing(1),
    },
  },
  search: {
    marginBottom: 10,
  },
  activeWarrant: {
    background: theme.palette.error.main,
    color: theme.card.bg,
    width: 'calc(100% + 32px)',
    display: 'inline-block',
    padding: '8px 16px',
    margin: '-8px -16px',
  },
  textField: {
    margin: '0 4px 8px',
  },
  list: {
    width: '100%',
    height: 500,
  },
  actions: {
    width: '100%',
    padding: theme.spacing(1, 0, 2),
    textAlign: 'right',
    '& button': {
      marginLeft: theme.spacing(1),
    },
  },
}));

const CapabilityColumns = [
  { field: 'CapabilityType', headerName: 'Type',      width: 150},
  { field: 'ComponentName',  headerName: 'Description', width: 300 },
];

function PersonalCapabilities(props) {
  const classes = useStyles();
  const { dictionary } = props;
  const { CapabilityTypes } = dictionary;
  const [view, setView] = useState('list');
  const [capability, setCapability] = useState(null);
  const [capabilities, setCapabilities] = useState([]);
  const [filteredCapabilities, setFilteredCapabilities] = useState([]);
  const [searchMatch, setSearchMatch] = useState([]);
  const [personData, setPersonData] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selection, setSelection] = useState([]);
  const throttleRef = useRef(0);
  const [selectedBeginningDate, setSelectedBeginningDate] = useState(new Date());
  const [selectedEndingDate, setSelectedEndingDate] = useState(null);

  useEffect(() => {
    if (!data) return;
    getCapabilities();
    // eslint-disable-next-line
  }, [data]);

  useEffect(() => {
    const filteredCapabilities = CapabilityTypes
      .filter(t => !capabilities.find(c => c.CapabilityType === t.Code) && t.Category === 'Personal')
    setFilteredCapabilities(filteredCapabilities);
    // eslint-disable-next-line
  }, [capabilities]);

  const getCapabilities = async () => {
    const { ptsPersonID } = data;
    setLoading(true);
    try {
      const capabilities = await getPersonalCapabilities(ptsPersonID);
      setCapabilities(capabilities.map(c => { return {...c, id: c.CapabilityType }}));
    } catch (err) {
      props.handleError(err, 'Error receiving capabilities');
    }
    setLoading(false);
  }

  const save = async () => {
    const CapabilityType = capability.Code;
    const ComponentName = capability.Description;
    const { ptsPersonID } = personData;
    props.showSpinner();
    try {
      await savePersonalCapability({ ptsPersonID, CapabilityType, ComponentName, selectedBeginningDate, selectedEndingDate });
      await getCapabilities();
      setView('list');
    } catch (err) {
      props.handleError(err, 'Error saving capability');
    }
    props.hideSpinner();
  }

  const onPersonSearchSelect = (ev, selection) => {
    if (selection) {
      setPersonData(selection);
      setData({ ...selection });
    } else {
      clearPersonData();
    }
  }

  const onCapabilitySelect = (ev, selection) => {
    setCapability(selection);
  }

  const clearPersonData = () => {
    setPersonData(null);
    setData(null);
    setSelection([]);
    setCapabilities([]);
  }

  const searchPerson = (text) => {
    clearTimeout(throttleRef.current);
    throttleRef.current = setTimeout(() => {
      data && clearPersonData();
      findPartyPerson(text)
      .then(result => setSearchMatch(result))
      .catch((err) => console.log(err));
    }, 500);
  }

  const renderPersonSearch = () => {
    return (
      <div className={classes.form}>
        <Autocomplete
          fullWidth
          disabled={view === 'add'}
          options={searchMatch}
          value={personData}
          getOptionLabel={(option) => option.FullName}
          className={classes.autocomplete}
          size="small"
          onChange={onPersonSearchSelect}
          onInputChange={(ev, val) => searchPerson(val)}
          renderInput={(params) => <TextField {...params} label="Search Person" variant="outlined" />}
          getOptionSelected={(option, value) => option.ptsPersonID === value.ptsPersonID}
          renderOption={option => <span
            className={option.ActiveWarrant ? classes.activeWarrant : null}>
              {option.LastName}, {option.FirstName} {option.PersonID}
            </span>
          }
        />
      </div>
    );
  }

  const handleBeginningDateChange = (date) => {
    setSelectedBeginningDate(date);
  };

  const handleEndingDateChange = (date) => {
    setSelectedEndingDate(date);
  };

  const renderAddCapability = () => {
    return (
      <>
        <Grid container>
          <Grid item md={12}>
            <div className={classes.form}>
              <Autocomplete
                fullWidth
                options={filteredCapabilities}
                value={capability}
                getOptionLabel={(option) => option.Code ? option.Code : ''}
                className={classes.autocomplete}
                size="small"
                onChange={onCapabilitySelect}
                renderInput={(params) => <TextField {...params} label="Capability" variant="outlined" />}
                getOptionSelected={(option, value) => option.Code === value.Code}
                renderOption={(option) => {
                  const { Code, Description } = option;
                  return (
                  <span><strong>{Code}</strong>{!!Description && <> - {Description}</>}</span>
                )}}
              />
            </div>
          </Grid>
          <div className={classes.form} style={{marginLeft: 0, marginTop: -8}}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                className = "mr-2"
                disableToolbar
                variant="inline"
                inputVariant="outlined"
                size="small"
                format="MM/dd/yyyy"
                margin="normal"
                label="Beginning"
                value={selectedBeginningDate}
                onChange={handleBeginningDateChange}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
              />
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                inputVariant="outlined"
                format="MM/dd/yyyy"
                margin="normal"
                size="small"
                label="Ending"
                value={selectedEndingDate}
                onChange={handleEndingDateChange}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
              />
            </MuiPickersUtilsProvider>
          </div>
          <div className={classes.buttons}>
            <Button 
              variant="contained"
              color="primary"
              disabled={!capability || !personData}
              onClick={save}
            >
              <SaveIcon />
              Save
            </Button>
            <Button 
              color="primary"
              onClick={() => {setView('list')}}
            >
              <CloseIcon />
              Close
            </Button>
          </div>
        </Grid>
      </>
    );
  }

  const handleSelectChange = ({selectionModel}) => {
    setSelection(selectionModel);
  }

  const delCapability = async () => {
    if (!window.confirm('Are you sure you want to remove this capability?')) return;
    const CapabilityType = selection[0];
    const { ptsPersonID } = data;
    setLoading(true);
    try {
      await removePersonalCapability({CapabilityType, ptsPersonID });
      await getCapabilities();
    } catch (err) {
      props.handleError(err, 'Error deleting capability');
    }
    setLoading(false);
  }

  const addCapability = () => {
    setView('add');
    setCapability(null);
  }

  const renderActions = () => {
    const delDisabled = selection.length === 0;
    return (
      <div className={classes.actions}>
        <Fab size="small" color="secondary" onClick={addCapability} disabled={!data}>
          <AddIcon />
        </Fab>
        <Fab size="small" color="secondary" onClick={delCapability} disabled={delDisabled}>
          <DeleteIcon />
        </Fab>
        {view !== 'list' && <Fab size="small" color="secondary" onClick={() => setView('list')}>
          <CloseIcon />
        </Fab>}
      </div>
    );
  }

  const renderCapabilities = () => {
    return (
      <div className={classes.list}>
        <XGrid
          columns={CapabilityColumns}
          rows={capabilities}
          loading={loading}
          rowHeight={38}
          onSelectionModelChange={handleSelectChange}
          disableSelectionOnClick={view === 'add'}
          disableMultipleSelection
          hideFooter
        />
      </div>
    );
  }

  return (
    <div className={classes.wrap}>
      {renderPersonSearch()}
      {view === 'add' && renderAddCapability()}
      {view === 'list' && renderActions()}
      {renderCapabilities()}
    </div>
  );
}

const mapStateToProps = state => {
  return {
    dictionary: state.dictionary
  }
}

export default connect(mapStateToProps, {
  handleError, showSpinner, hideSpinner 
})(PersonalCapabilities);
